<template>
  <div class="d-flex flex-column" data-cy="zapier-keys">
    <div v-if="tabs.length > 1" class="mb-4 flex gap-2" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        role="tab"
        :aria-selected="activeTab === tab.id"
        :class="activeTab === tab.id ? TAB_ACTIVE : TAB_IDLE"
        :data-cy="`zapier-tab-${tab.id}`"
        @click="activeTab = tab.id"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-if="activeTab === 'v2'" class="space-y-4" data-cy="zapier-two-view">
      <p class="m-0 text-sm text-gray-700">
        Zapier connects with an API key and subscribes to Sera events that you
        can create workflows around. The Sera events provide IDs for things like
        customers and appointments. In Zapier you can then look up details using
        those IDs in the Find actions. You can generate API keys here that have
        the permissions needed to look up the details provided.
      </p>

      <Alert v-if="readOnly" type="error" :text="READ_ONLY_COPY" />
      <Alert v-if="error" type="error" :text="error" data-cy="zapier-error" />

      <div v-if="key" class="space-y-3" data-cy="zapier-key-shown">
        <Alert type="warning" data-cy="zapier-key-once">
          <strong>{{ API_KEY_SHOWN_ONCE }}</strong>
        </Alert>
        <div class="flex flex-wrap items-center gap-2">
          <code
            class="min-w-0 flex-1 break-all rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-900"
            data-cy="zapier-key-value"
            >{{ key }}</code
          >
          <button
            type="button"
            :class="SMALL_SECONDARY"
            data-cy="zapier-key-copy"
            @click="copy(key)"
          >
            {{ copied ? "Copied" : "Copy" }}
          </button>
        </div>
        <p class="m-0 text-sm text-gray-600">
          Store it where you keep passwords. If it's lost, make a new key and
          revoke this one.
        </p>
      </div>

      <section class="space-y-2" data-cy="zapier-key-list">
        <p
          v-if="!keys.length"
          class="m-0 text-sm text-gray-700"
          data-cy="zapier-no-keys"
        >
          You don't have a Zapier 2.0 key yet.
        </p>

        <div
          v-for="item in keys"
          :key="item.id"
          class="rounded-lg border border-gray-200 bg-white p-3"
          :data-cy="`zapier-key-row:${item.id}`"
        >
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="text-sm text-gray-900">
              <code data-cy="zapier-key-prefix">{{ item.token_prefix }}…</code>
              <span class="ml-2 text-gray-500"
                >made {{ day(item.created_at) }}, {{ lastUsed(item) }}</span
              >
            </div>
            <div class="flex gap-2">
              <button
                v-if="item.missing_access.length"
                type="button"
                :class="SMALL_PRIMARY"
                :disabled="readOnly || busy !== null"
                :data-cy="`zapier-add-access:${item.id}`"
                @click="addAccess(item)"
              >
                {{ busy === item.id ? "Adding..." : "Add access" }}
              </button>
              <button
                v-if="revoking !== item.id"
                type="button"
                :class="SMALL_SECONDARY"
                :disabled="busy !== null"
                :data-cy="`zapier-revoke:${item.id}`"
                @click="revoking = item.id"
              >
                Revoke
              </button>
            </div>
          </div>

          <p
            class="m-0 mt-1 text-xs text-gray-600"
            :data-cy="`zapier-key-version:${item.id}`"
          >
            {{ versionNote(item) }}
          </p>

          <Alert
            v-if="item.missing_access.length"
            type="warning"
            class="mt-2"
            :data-cy="`zapier-missing-access:${item.id}`"
          >
            <span
              >Newer Sera events need access this key doesn't have yet:
              <strong>{{
                item.missing_access.map((a) => a.name).join(", ")
              }}</strong
              >. Add it to this key: Zaps connected with it keep working and can
              use the new events.</span
            >
          </Alert>

          <Alert
            v-if="revoking === item.id"
            type="warning"
            class="mt-2"
            :data-cy="`zapier-revoke-warning:${item.id}`"
          >
            <span
              ><strong
                >This key stops working as soon as you revoke it. Zaps connected
                with it will fail until you reconnect them with another
                key.</strong
              >
              <button
                type="button"
                :class="[SMALL_DANGER, 'ml-2']"
                :disabled="busy !== null"
                :data-cy="`zapier-revoke-confirm:${item.id}`"
                @click="revoke(item)"
              >
                {{ busy === item.id ? "Revoking..." : "Revoke key" }}
              </button>
              <button
                type="button"
                :class="[SMALL_SECONDARY, 'ml-2']"
                @click="revoking = null"
              >
                Cancel
              </button></span
            >
          </Alert>
        </div>
      </section>

      <div class="flex justify-end">
        <button
          type="button"
          :class="SMALL_PRIMARY"
          :disabled="readOnly || busy !== null"
          data-cy="zapier-generate"
          @click="generate"
        >
          {{
            busy === "new"
              ? "Generating..."
              : keys.length
                ? "Generate another key"
                : "Generate key"
          }}
        </button>
      </div>
    </div>

    <div v-else class="space-y-4" data-cy="zapier-one-view">
      <Alert type="info" data-cy="zapier-upgrade-note">
        <strong>Zapier 2.0 is here.</strong> It replaces this key with a
        <code>sera_ik_</code> key and a new set of triggers. Zaps you built
        already keep working on 1.x with the key below, and they don't change
        when you start using 2.0. Don't move an existing Zap to 2.0: the data it
        receives is different, so its steps would break. Build new Zaps on 2.0
        instead.
      </Alert>
      <div class="d-flex flex-column">
        <InputField
          :model-value="zapier.api_key"
          :disabled="true"
          title="Zapier 1.x API Key"
          placeholder="API Key"
        />
      </div>
      <div class="flex justify-end">
        <button
          type="button"
          :class="SMALL_SECONDARY"
          data-cy="zapier-one-copy"
          @click="copy(zapier.api_key)"
        >
          {{ copied ? "Copied to clipboard" : "Copy API Key" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from "vue";
import { useStore } from "vuex";

import Alert from "@/components/Seranova/Alert/Alert.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import { API_KEY_SHOWN_ONCE } from "../ApiAccess/apiAccess";
import { apiError } from "../Webhooks/webhooksHub";
import type { ZapierIntegrations, ZapierKey } from "./zapierKey";

const props = defineProps<{ zapierIntegrations: ZapierIntegrations }>();

const SMALL_SECONDARY =
  "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50";
const SMALL_PRIMARY =
  "rounded-lg bg-blue-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50";
const SMALL_DANGER =
  "rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50";
const TAB_ACTIVE =
  "rounded-lg bg-blue-700 px-3 py-1.5 text-sm font-medium text-white";
const TAB_IDLE =
  "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100";
const READ_ONLY_COPY =
  "Your account is read-only, so Zapier keys can't be created or given more access. Existing keys can still be revoked.";

const store = useStore();

const zapier = computed(() => props.zapierIntegrations.zapier);
const zapierV2 = computed(() => props.zapierIntegrations.zapier_v2);
const keys = computed<ZapierKey[]>(() => zapierV2.value.keys ?? []);
const key = computed(() => zapierV2.value.key ?? null);
const readOnly = computed(() => !!zapierV2.value.read_only);

// 2.0 is offered when the api says so; the 1.x key shows for tenants that have
// it (every tenant while 2.0 is not offered).
const tabs = computed(() => {
  const list: { id: "v2" | "v1"; label: string }[] = [];
  if (zapierV2.value.available) list.push({ id: "v2", label: "Zapier 2.0" });
  if (zapier.value.api_key) list.push({ id: "v1", label: "Zapier 1.x" });
  return list;
});
const activeTab = ref<"v2" | "v1">(tabs.value[0]?.id ?? "v1");

// "new" while a key is being made, else the id of the key being changed.
const busy = ref<string | null>(null);
const revoking = ref<string | null>(null);
const error = ref("");
const copied = ref(false);

const day = (time: string) => new Date(time).toLocaleDateString();
const lastUsed = (item: ZapierKey) =>
  item.last_used_at ? `last used ${day(item.last_used_at)}` : "never used";

// Which Zapier app version the key's permissions were worked out for, and
// whether the app has needed more since.
function versionNote(item: ZapierKey): string {
  if (!item.app_version) {
    return "Made before the Zapier app version was recorded.";
  }
  const base = `Permissions chosen for Zapier app ${item.app_version}.`;
  if (item.missing_access.length) {
    const n = item.missing_access.length;
    return `${base} The app (${zapierV2.value.app_version}) needs ${n} more permission${n === 1 ? "" : "s"}.`;
  }
  return `${base} Up to date.`;
}

// A new key is shown once: leaving the tab drops it from the store, so coming
// back shows the prefix.
onBeforeUnmount(() => {
  if (key.value) store.dispatch("fetchZapierIntegrations");
});

// Runs a change, keeping the error.
async function run(
  who: string,
  action: string,
  params: Record<string, unknown>,
  failure: string
) {
  error.value = "";
  busy.value = who;
  try {
    await store.dispatch(action, params);
    revoking.value = null;
  } catch (e) {
    error.value = apiError(e, failure).message;
  } finally {
    busy.value = null;
  }
}

const generate = () =>
  run(
    "new",
    "generateZapierApiKey",
    {},
    "The key couldn't be generated. Try again."
  );

const addAccess = (item: ZapierKey) =>
  run(
    item.id,
    "addZapierApiKeyAccess",
    { id: item.id },
    "The access couldn't be added. Try again."
  );

const revoke = (item: ZapierKey) =>
  run(
    item.id,
    "revokeZapierApiKey",
    { id: item.id },
    "The key couldn't be revoked. Try again."
  );

async function copy(value: string | null | undefined) {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    copied.value = true;
    setTimeout(() => (copied.value = false), 2000);
  } catch {
    window.notify.error("Couldn't copy. Select the key and copy it instead.");
  }
}
</script>
